import { ChangeDetectionStrategy, Component, Input, OnInit } from '@angular/core';

import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles/enums';
import { BehaviorSubject, EMPTY, Observable, of } from 'rxjs';
import { catchError, map, take } from 'rxjs/operators';
import { Store } from '@ngrx/store';

import { finShare } from '@fcom/rx';
import { LanguageService } from '@fcom/ui-translate';
import { RootPaths } from '@fcom/core/constants';
import {
  ButtonSize,
  ButtonTheme,
  ButtonType,
  IconPosition,
  LoaderTheme,
  NotificationLayout,
  NotificationTheme,
} from '@fcom/ui-components';
import { AppState, ConfigService } from '@fcom/core';
import { profile } from '@fcom/core/selectors';
import { ProfileTier } from '@fcom/core-api/login';

import { Voucher, VoucherProductName } from '../../interfaces';
import { UpgradeBenefitsState, VoucherService } from '../../services/voucher.service';

interface VoucherAmountByDate {
  productName: VoucherProductName;
  date: string;
  amount: number;
}

export enum BenefitCategory {
  UPGRADE = 'upgrade',
  OTHER = 'other',
}

interface BenefitGroup {
  benefitName: string;
  productName: string;
  amount: number;
  changed: boolean;
  vouchers: VoucherAmountByDate[];
  type: BenefitCategory;
  date: string;
}

@Component({
  selector: 'fin-upgrade-benefits',
  templateUrl: './upgrade-benefits.component.html',
  styleUrls: ['./upgrade-benefits.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class UpgradeBenefitsComponent implements OnInit {
  readonly BenefitCategory = BenefitCategory;
  readonly ButtonSize = ButtonSize;
  readonly ButtonTheme = ButtonTheme;
  readonly ButtonType = ButtonType;
  readonly IconPosition = IconPosition;
  readonly LoaderTheme = LoaderTheme;
  readonly NotificationLayout = NotificationLayout;
  readonly NotificationTheme = NotificationTheme;
  readonly SvgLibraryIcon = SvgLibraryIcon;
  readonly UpgradeBenefitsState = UpgradeBenefitsState;

  benefits$: Observable<BenefitGroup[]>;
  oneUpgradeUrl$: Observable<string>;
  landingPageUrl$: Observable<string>;
  displayedVoucherCount$: BehaviorSubject<number> = new BehaviorSubject(3);
  upgradeState$: Observable<UpgradeBenefitsState>;
  tier$: Observable<ProfileTier>;
  finnairShopUrl: string;

  upgradeVouchers$: Observable<BenefitGroup[]>;
  otherVouchers$: Observable<BenefitGroup[]>;

  @Input()
  vouchers$: Observable<Voucher[]>;

  @Input()
  showLoading$: Observable<boolean> = of(false);

  constructor(
    private languageService: LanguageService,
    private voucherService: VoucherService,
    private store$: Store<AppState>,
    private configService: ConfigService
  ) {}

  ngOnInit(): void {
    this.tier$ = this.store$.pipe(
      profile(),
      take(1),
      map((x) => x.tier)
    );
    this.upgradeState$ = this.voucherService.status$;
    this.benefits$ = this.vouchers$.pipe(
      map((vouchers: Voucher[]) => this.groupVouchersByName(vouchers).map(this.getVoucherAmountsGroupedByDate).flat()),
      map((vouchers: VoucherAmountByDate[]) => this.groupVouchersByBenefitName(vouchers)),
      catchError(() => EMPTY),
      finShare()
    );
    this.upgradeVouchers$ = this.benefits$.pipe(
      map((vouchers) => vouchers.filter(({ type }) => type === BenefitCategory.UPGRADE))
    );
    this.otherVouchers$ = this.benefits$.pipe(
      map((vouchers) => vouchers.filter(({ type }) => type === BenefitCategory.OTHER))
    );

    this.oneUpgradeUrl$ = this.languageService.lang.pipe(map((lan) => `/${lan}/${RootPaths.ONE_UPGRADE_ROOT}`));
    this.landingPageUrl$ = this.languageService.lang.pipe(map((lan) => `/${lan}/${RootPaths.BOOKING_ROOT}`));
    const lang = this.languageService.langKeyValue;
    this.finnairShopUrl = this.configService.cfg.finnairShopUrl.replace(
      '{lang}',
      !['en', 'fi', 'sv', 'ja', 'ko', 'zh'].includes(lang) ? 'en' : lang
    );
  }

  private groupVouchersByName(vouchers: Voucher[]): Voucher[][] {
    return Object.values(
      vouchers.reduce((vouchersByName: { [key: string]: Voucher[] }, voucher: Voucher) => {
        if (!vouchersByName[voucher.productName]) {
          vouchersByName[voucher.productName] = [];
        }
        vouchersByName[voucher.productName].push(voucher);
        return vouchersByName;
      }, {})
    );
  }

  private getVoucherAmountsGroupedByDate(vouchers: Voucher[]): VoucherAmountByDate[] {
    return vouchers
      .reduce((dates: VoucherAmountByDate[], voucher: Voucher) => {
        const existing = dates.find((date) => date.date === voucher.expirationDate);
        if (existing) {
          existing.amount += voucher.amount;
        } else {
          dates.push({
            productName: voucher.productName,
            date: voucher.expirationDate,
            amount: voucher.amount,
          });
        }
        return dates;
      }, [])
      .sort((a: VoucherAmountByDate, b: VoucherAmountByDate) => (a.date > b.date ? 1 : b.date > a.date ? -1 : 0));
  }

  private groupVouchersByBenefitName(vouchers: VoucherAmountByDate[]): BenefitGroup[] {
    return vouchers.reduce((all, voucher) => {
      const benefitName = voucher.productName;
      const benefit = all.find((item) => item.benefitName === benefitName);
      if (benefit) {
        benefit.vouchers = benefit.vouchers.concat(voucher);
        benefit.amount += voucher.amount;
      } else {
        all.push({
          benefitName,
          amount: voucher.amount,
          changed: false,
          vouchers: [voucher],
          type: [
            VoucherProductName.EUROPE_UPGRADE,
            VoucherProductName.UPGRADE_BENEFIT,
            VoucherProductName.LONG_HAUL_UPGRADE,
          ].includes(benefitName)
            ? BenefitCategory.UPGRADE
            : BenefitCategory.OTHER,
          date: voucher.date,
        });
      }
      return all;
    }, []);
  }
}
